import type { HTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';
import styled from 'styled-components';
import { colors } from '../../../../styles/colors';
import { documentHyphenation, fontFamily, radii, space, text } from '../../../../styles/onboardingUiTokens';

export type NoticePreset = 'default' | 'info' | 'success' | 'warning' | 'error';

const accentByPreset: Record<NoticePreset, string> = {
  default: colors.medium,
  info: colors.primary,
  success: colors.success,
  warning: colors.delay,
  error: colors.lightRed,
};

/** Darker, readable-on-tint text color per preset (Figma flickflack card, ERS-61 A2). Presets without a dedicated dark text color fall back to their accent. */
const darkTextByPreset: Record<NoticePreset, string> = {
  default: colors.medium,
  info: colors.primaryDarkText,
  success: colors.success,
  warning: colors.delay,
  error: colors.lightRed,
};

const Root = styled.div<{ $accent: string }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${space.xs};
  padding: ${space.sm};
  border-radius: ${radii.control};
  background: color-mix(in srgb, ${({ $accent }) => $accent} 15%, ${colors.white});
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${space.sm};
  min-width: 0;
`;

const IconSlot = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  margin-top: 0.32em;
`;

const TitleText = styled.p<{ $accent: string }>`
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.small.fontSize};
  line-height: ${text.small.lineHeight};
  font-weight: 600;
  color: ${({ $accent }) => $accent};
`;

const BodySlot = styled.div<{ $besideIcon?: boolean; $color: string }>`
  margin: 0;
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.body.fontSize};
  line-height: ${text.body.lineHeight};
  font-weight: ${text.body.fontWeight};
  color: ${({ $color }) => $color};
  ${documentHyphenation}
  ${({ $besideIcon }) =>
    $besideIcon
      ? `
    flex: 1 1 auto;
    min-width: 0;
  `
      : ''}
`;

export type NoticeProps = HTMLAttributes<HTMLDivElement> & {
  /** Visual intent; drives accent, title color, and background tint. */
  preset?: NoticePreset;
  /** Short heading in the accent color; optional. */
  title?: string;
  /** Optional leading graphic; mark decorative icons with `aria-hidden` on the child or use kit `SvgIcon`. */
  icon?: ReactNode;
  /** Body text color: `'dark'` (default, unchanged) or `'accent'` for the preset's dark text color (Figma flickflack card). */
  bodyTone?: 'dark' | 'accent';
  children: ReactNode;
};

export function Notice({
  preset = 'default',
  title,
  icon,
  bodyTone = 'dark',
  children,
  className,
  'aria-labelledby': ariaLabelledByProp,
  'aria-label': ariaLabelProp,
  ...rest
}: NoticeProps) {
  const titleId = `notice-title-${useId()}`;
  const accent = accentByPreset[preset];
  const bodyColor = bodyTone === 'accent' ? darkTextByPreset[preset] : colors.dark;
  const hasTitle = Boolean(title);
  const hasIcon = Boolean(icon);
  const labelledBy = ariaLabelledByProp ?? (title ? titleId : undefined);
  const isRegion = Boolean(labelledBy) || Boolean(ariaLabelProp);

  return (
    <Root
      {...rest}
      className={className}
      $accent={accent}
      role={isRegion ? 'region' : undefined}
      aria-labelledby={labelledBy}
      aria-label={labelledBy ? undefined : ariaLabelProp}
    >
      {hasTitle ? (
        <>
          <TopRow>
            {hasIcon ? <IconSlot>{icon}</IconSlot> : null}
            <TitleText id={titleId} $accent={accent}>
              {title}
            </TitleText>
          </TopRow>
          <BodySlot $color={bodyColor}>{children}</BodySlot>
        </>
      ) : hasIcon ? (
        <TopRow>
          <IconSlot>{icon}</IconSlot>
          <BodySlot $besideIcon $color={bodyColor}>
            {children}
          </BodySlot>
        </TopRow>
      ) : (
        <BodySlot $color={bodyColor}>{children}</BodySlot>
      )}
    </Root>
  );
}
